import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Lock, Vote } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common/Brand";
import { fmtMeetingDate, getDeviceId } from "@/components/meeting/meetUtils";

export default function PublicVotePage() {
  const { publicId } = useParams();
  const [poll, setPoll] = useState(null);
  const [error, setError] = useState("");
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const device = getDeviceId();

  const load = async () => {
    try {
      const { data } = await api.get(`/public/polls/${publicId}`, { params: { device_id: device } });
      setPoll(data);
      setError("");
    } catch (e) {
      setError(errMsg(e, "Voting tidak ditemukan."));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);
  // refresh results after voting
  useEffect(() => {
    if (!poll?.results) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!poll?.results]);

  const vote = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/polls/${publicId}/vote`, { option_id: choice, device_id: device });
      setPoll(data);
    } catch (e) {
      setError(errMsg(e, "Gagal mengirim suara"));
      load();
    } finally {
      setBusy(false);
    }
  };

  const voted = poll?.voted_option_id;
  const total = poll?.results?.total || 0;

  return (
    <div className="relative z-[1] min-h-screen flex flex-col" data-testid="public-vote-page">
      <header className="px-5 sm:px-8 py-5 border-b border-border"><Logo /></header>
      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-8">
        <div className="w-full max-w-xl rounded-2xl border border-[color:var(--hairline)] bg-card elev-2 p-6 sm:p-8">
          {!poll && !error && (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          )}
          {error && !poll && <p className="text-sm text-[hsl(0_72%_68%)]" data-testid="public-vote-error">{error}</p>}
          {poll && (
            <>
              <div className="eyebrow flex items-center gap-2"><Vote className="h-3.5 w-3.5" /> {poll.type_label} · {fmtMeetingDate(poll.date)}</div>
              <h1 className="font-display text-3xl mt-2 leading-tight" data-testid="public-vote-question">{poll.question}</h1>
              {poll.status === "closed" && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Voting sudah ditutup</div>
              )}

              {!poll.results ? (
                <div className="mt-6 space-y-2.5" role="radiogroup">
                  {poll.options.map((o, i) => {
                    const on = choice === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setChoice(o.id)}
                        className={`press w-full text-left min-h-[52px] rounded-xl border px-4 py-3 flex items-center gap-3 transition-colors ${on ? "border-primary bg-[hsl(43_30%_9%)]" : "border-border bg-[hsl(220_12%_8%)] hover:border-[hsl(43_35%_35%)]"}`}
                        data-testid={`public-vote-option-${i}`}
                      >
                        <span className={`h-7 w-7 rounded-full border flex items-center justify-center text-xs num shrink-0 ${on ? "border-primary bg-primary text-primary-foreground" : "border-[hsl(220_10%_26%)] text-muted-foreground"}`}>{String.fromCharCode(65 + i)}</span>
                        <span className="text-[15px]">{o.text}</span>
                      </button>
                    );
                  })}
                  {error && <p className="text-sm text-[hsl(0_72%_68%)]" data-testid="public-vote-error">{error}</p>}
                  <Button onClick={vote} disabled={!choice || busy} className="w-full h-12 mt-3 press text-base" data-testid="public-vote-submit">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Vote className="h-4 w-4 mr-2" />} Kirim suara
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center">Anonim · 1 suara per perangkat</p>
                </div>
              ) : (
                <div className="mt-6 space-y-3" data-testid="public-vote-results">
                  {voted && (
                    <div className="flex items-center gap-2 text-sm text-[hsl(160_55%_60%)]" data-testid="public-vote-thanks"><CheckCircle2 className="h-4 w-4" /> Terima kasih, suara Anda sudah tercatat.</div>
                  )}
                  {poll.options.map((o, i) => {
                    const n = poll.results.counts?.[o.id] || 0;
                    const pct = total ? Math.round((n / total) * 100) : 0;
                    const mine = voted === o.id;
                    return (
                      <div key={o.id} data-testid={`public-vote-result-${i}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className={mine ? "text-primary" : ""}>{String.fromCharCode(65 + i)}. {o.text} {mine && <span className="text-[11px]">(pilihan Anda)</span>}</span>
                          <span className="num text-xs text-muted-foreground">{pct}%</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-[hsl(220_10%_14%)] mt-1 overflow-hidden">
                          <div className={`h-full rounded-full transition-[width] duration-500 ${mine ? "bg-primary" : "bg-[hsl(43_35%_38%)]"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-muted-foreground num pt-1" data-testid="public-vote-total">{total} suara masuk</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <footer className="px-5 py-4 text-center text-xs text-muted-foreground border-t border-border">kalkulatoreddy.com</footer>
    </div>
  );
}
